import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { Card, Field, Input, Textarea, PageHeader, PrimaryButton, DangerButton } from "@/components/admin/AdminUI";
import { CloudinaryUploader } from "@/components/CloudinaryUploader";
import { SeoGenerateButton } from "@/components/admin/SeoGenerateButton";

const EMPTY = {
  name: "", description: "", external_link: "", logo_url: "",
  seo_title: "", seo_description: "", is_public: true,
};

export default function AdminBrandCertificationEdit() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const { data: cert } = useQuery({
    queryKey: ["admin-brand-certification", id],
    queryFn: async () => (await api.get(`/admin/brand-certifications/${id}`)).data,
    enabled: !!id,
  });

  useEffect(() => { if (cert) setForm({ ...EMPTY, ...cert }); }, [cert]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : (e.target ? e.target.value : e) }));

  async function save() {
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post("/admin/brand-certifications", form);
        toast.success("Brand certification created");
        qc.invalidateQueries({ queryKey: ["admin-brand-certifications"] });
        navigate(`/admin/brand-certifications/${res.data.id}`);
      } else {
        await api.patch(`/admin/brand-certifications/${id}`, form);
        toast.success("Saved");
        qc.invalidateQueries({ queryKey: ["admin-brand-certifications"] });
        qc.invalidateQueries({ queryKey: ["admin-brand-certification", id] });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }

  async function remove() {
    if (!window.confirm("Delete this brand certification? It will also be detached from any brands currently using it.")) return;
    await api.delete(`/admin/brand-certifications/${id}`);
    toast.success("Deleted");
    qc.invalidateQueries({ queryKey: ["admin-brand-certifications"] });
    navigate("/admin/brand-certifications");
  }

  return (
    <div data-testid="admin-brand-certification-edit">
      <PageHeader
        title={isNew ? "New brand certification" : (form.name || "Certification")}
        subtitle={isNew ? "" : `/brand-certifications/${form.slug || ""}`}
        actions={<>
          <PrimaryButton onClick={save} disabled={saving} data-testid="brand-cert-save-button">{saving ? "Saving…" : "Save"}</PrimaryButton>
          {!isNew && <DangerButton onClick={remove}>Delete</DangerButton>}
        </>}
      />

      <Card className="space-y-4">
        <Field label="Title (Name)"><Input value={form.name} onChange={set("name")} data-testid="brand-cert-name-input" /></Field>

        <Field label="Description">
          <Textarea rows={6} value={form.description} onChange={set("description")} data-testid="brand-cert-description-input" />
        </Field>

        <Field label="Link to organization website">
          <Input value={form.external_link || ""} onChange={set("external_link")} placeholder="https://bcorporation.net" data-testid="brand-cert-link-input" />
        </Field>

        <Field label="Logo" hint="Upload a file or paste a URL — both go through the same uploader.">
          <div className="flex items-center gap-3">
            <Input value={form.logo_url || ""} onChange={set("logo_url")} placeholder="https://…" data-testid="brand-cert-logo-url-input" />
            <CloudinaryUploader
              mode="admin"
              folder="sustainably_curated/brand_certifications"
              multiple={false}
              label={form.logo_url ? "Replace" : "Upload"}
              onUploaded={(url) => setForm((f) => ({ ...f, logo_url: url }))}
            />
          </div>
          {form.logo_url && (
            <div className="mt-3 relative inline-block">
              <img src={form.logo_url} alt="Logo preview" className="h-20 w-auto rounded-md border border-[#E9ECEF] bg-white object-contain p-2" />
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, logo_url: "" }))}
                className="absolute -top-2 -right-2 bg-white border border-[#E9ECEF] rounded-full p-1 shadow hover:bg-red-50 hover:border-red-200"
                data-testid="brand-cert-logo-clear"
                aria-label="Remove logo"
              >
                <X className="w-3.5 h-3.5 text-red-600" />
              </button>
            </div>
          )}
        </Field>

        <div className="flex items-center justify-between"><span className="text-sm">Public</span><input type="checkbox" checked={!!form.is_public} onChange={set("is_public")} data-testid="brand-cert-public-checkbox" /></div>

        <div className="border-t border-[#E9ECEF] pt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs uppercase tracking-wider text-[#6C757D]">SEO</h3>
            <SeoGenerateButton
              buildPayload={() => ({ kind: "certification", name: form.name, short_description: form.description?.slice(0, 200), description: form.description })}
              onResult={({ seo_title, seo_description }) => setForm((f) => ({ ...f, seo_title, seo_description }))}
            />
          </div>
          <Field label="SEO title"><Input value={form.seo_title || ""} onChange={set("seo_title")} maxLength={70} data-testid="brand-cert-seo-title-input" /></Field>
          <Field label="SEO description"><Textarea rows={2} value={form.seo_description || ""} onChange={set("seo_description")} maxLength={170} data-testid="brand-cert-seo-desc-input" /></Field>
        </div>
      </Card>
    </div>
  );
}
